import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { formatChartDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import BloodTrendChart from './shared/BloodTrendChart';
import SupplementTimeline from './SupplementTimeline';
import { UNITS } from '../utils/unitFormatter';
import { computePageDomain } from '../utils/resultsDomain';
import TimeWindowSelector, { filterByTimeWindow, filterTreatmentsByTimeWindow } from './TimeWindowSelector';

/**
 * BloodGraphs - Blood marker chart with treatment timeline
 * 
 * CRITICAL: X-axis domain is computed from FILTERED results only.
 * Time window filtering is presentation-layer only.
 * Tabs change Y-data only — X-axis is SHARED across all tabs.
 * 
 * STICKY TABS: Uses pure CSS position:sticky for smooth, jitter-free behavior.
 * 
 * TREATMENT OVERLAY: Tapping a treatment bar shows start/end markers on the chart.
 */
const BloodGraphs = ({ results, clinicalRanges, supplements = [] }) => {
  const { user } = useAuth();
  const country = user?.country || 'AU';
  const [activeMarker, setActiveMarker] = useState('wbc');
  const [timeWindow, setTimeWindow] = useState('36m'); // Default to 36 months view
  const [chartAxisConfig, setChartAxisConfig] = useState(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true); // Show hint when at start of chart
  const [selectedTreatment, setSelectedTreatment] = useState(null); // Selected treatment for overlay
  
  // Refs for synchronized scrolling
  const chartScrollRef = useRef(null);
  const timelineScrollRef = useRef(null);
  const isScrolling = useRef(false);
  const isInitialScrollDone = useRef(false); // Track if initial auto-scroll is complete
  
  // Auto-scroll chart to the right (most recent date / today) on mount AND when time window changes
  useEffect(() => {
    // Reset hint and scroll state when time window changes
    setShowSwipeHint(true);
    isInitialScrollDone.current = false;
    
    const scrollToRight = () => {
      const chartContainer = chartScrollRef.current;
      const timelineContainer = timelineScrollRef.current;
      
      if (chartContainer) {
        chartContainer.scrollLeft = chartContainer.scrollWidth;
      }
      if (timelineContainer) {
        timelineContainer.scrollLeft = timelineContainer.scrollWidth;
      }
      
      // Mark initial scroll as done after a brief delay
      setTimeout(() => {
        isInitialScrollDone.current = true;
      }, 300);
    };
    
    // Small delay to ensure chart is rendered
    const timer = setTimeout(scrollToRight, 150);
    return () => clearTimeout(timer);
  }, [timeWindow]); // Run on mount AND when time window changes
  
  // Synchronized horizontal scroll handler
  const handleHorizontalScroll = useCallback((source) => {
    if (isScrolling.current) return;
    isScrolling.current = true;
    
    const chartContainer = chartScrollRef.current;
    const timelineContainer = timelineScrollRef.current;
    
    // Only process hint logic if this is a USER scroll (not the initial auto-scroll)
    if (isInitialScrollDone.current && chartContainer) {
      const maxScroll = chartContainer.scrollWidth - chartContainer.clientWidth;
      // Check if at the right end (most recent data) - within 50px of max scroll
      const isAtEnd = chartContainer.scrollLeft >= maxScroll - 50;
      // Check if scrolled away from right end
      const isScrolledAway = chartContainer.scrollLeft < maxScroll - 100;
      
      if (isAtEnd) {
        setShowSwipeHint(true);
      } else if (isScrolledAway) {
        setShowSwipeHint(false);
      }
    }
    
    if (chartContainer && timelineContainer) {
      if (source === 'chart') {
        timelineContainer.scrollLeft = chartContainer.scrollLeft;
      } else {
        chartContainer.scrollLeft = timelineContainer.scrollLeft;
      }
    }
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 50);
  }, []);

  // Handle treatment selection - toggle on/off
  const handleTreatmentSelect = useCallback((treatment) => {
    setSelectedTreatment(prev => 
      prev?.id === treatment?.id ? null : treatment
    );
  }, []);

  // Clear treatment selection when clicking outside
  const handleChartClick = useCallback((e) => {
    // Only clear if clicking on the chart area, not on a data point
    if (e.target === e.currentTarget || e.target.tagName === 'svg') {
      setSelectedTreatment(null);
    }
  }, []);

  // Filter results and treatments by time window (presentation-layer only)
  // NOTE: We still use filtered results to check which markers have data
  const filteredResults = useMemo(() => {
    return filterByTimeWindow(results, timeWindow);
  }, [results, timeWindow]);

  const filteredSupplements = useMemo(() => {
    return filterTreatmentsByTimeWindow(supplements, results, timeWindow);
  }, [supplements, results, timeWindow]);

  // Compute X-axis domain using ALL results but FIXED time window
  // For 12m/24m/36m: domain is (today - X months) to today
  // For All: domain is earliest data to today
  const pageDomain = useMemo(() => {
    return computePageDomain(results, supplements, country, timeWindow);
  }, [results, supplements, country, timeWindow]);

  const getMarkers = () => {
    if (!clinicalRanges) {
      // Default ranges using centralized unit formatter
      return [
        { key: 'wbc', label: 'WBC', color: '#0F766E', unit: UNITS.BLOOD_COUNT, range: { min: 4.0, max: 11.0 } },
        { key: 'lymphocytes', label: 'Lymphocytes', color: '#059669', unit: UNITS.BLOOD_COUNT, range: { min: 1.0, max: 4.0 } },
        { key: 'platelets', label: 'Platelets', color: '#0369A1', unit: UNITS.BLOOD_COUNT, range: { min: 150, max: 400 } },
        { key: 'haemoglobin', label: 'Haemoglobin', color: '#D97706', unit: UNITS.HEMOGLOBIN_SI, range: { min: 130, max: 180 } },
        { key: 'neutrophils', label: 'Neutrophils', color: '#BE123C', unit: UNITS.BLOOD_COUNT, range: { min: 2.0, max: 7.5 } }
      ];
    }
    
    return [
      { key: 'wbc', label: 'WBC', color: '#0F766E', unit: clinicalRanges.wbc.unit, range: { min: clinicalRanges.wbc.min, max: clinicalRanges.wbc.max } },
      { key: 'lymphocytes', label: 'Lymphocytes', color: '#059669', unit: clinicalRanges.lymphocytes.unit, range: { min: clinicalRanges.lymphocytes.min, max: clinicalRanges.lymphocytes.max } },
      { key: 'platelets', label: 'Platelets', color: '#0369A1', unit: clinicalRanges.platelets.unit, range: { min: clinicalRanges.platelets.min, max: clinicalRanges.platelets.max } },
      { key: 'haemoglobin', label: 'Haemoglobin', color: '#D97706', unit: clinicalRanges.haemoglobin.unit, range: { min: clinicalRanges.haemoglobin.min, max: clinicalRanges.haemoglobin.max } },
      { key: 'neutrophils', label: 'Neutrophils', color: '#BE123C', unit: clinicalRanges.neutrophils.unit, range: { min: clinicalRanges.neutrophils.min, max: clinicalRanges.neutrophils.max } }
    ];
  };

  const markers = getMarkers();

  // Helper to check if a marker has data (in filtered results)
  const hasDataForMarker = (markerKey) => {
    return filteredResults.some(r => r[markerKey] != null);
  };

  // Sort markers: ones with data first, then empty ones (maintaining relative order within each group)
  const sortedMarkers = [...markers].sort((a, b) => {
    const aHasData = hasDataForMarker(a.key);
    const bHasData = hasDataForMarker(b.key);
    if (aHasData && !bHasData) return -1;
    if (!aHasData && bHasData) return 1;
    return 0;
  });

  const markerConfig = markers.find(m => m.key === activeMarker);

  // Prepare chart data - data within window + ONE point before for line continuity
  const chartData = useMemo(() => {
    if (!pageDomain) return [];
    
    const domainStart = pageDomain.domainStartTs;
    const domainEnd = pageDomain.domainEndTs;
    
    // Get all data points for this marker, sorted by date
    const allData = results
      .filter(r => r[activeMarker] != null)
      .map(r => ({
        date: formatChartDate(r.date, country),
        fullDate: r.date,
        timestamp: new Date(r.date).getTime(),
        value: r[activeMarker],
        isDuringIllness: r.is_during_illness || false // Include illness flag
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
    
    if (allData.length === 0) return [];
    
    // Get data within the domain window
    const windowData = allData.filter(d => d.timestamp >= domainStart && d.timestamp <= domainEnd);
    
    // Find ONE point BEFORE the window for line continuity
    // This makes the line enter from the left edge if there's earlier data
    const pointBefore = allData.filter(d => d.timestamp < domainStart).pop();
    
    // Combine: one point before (if exists) + window data
    if (pointBefore && windowData.length > 0) {
      return [pointBefore, ...windowData];
    }
    
    return windowData.length > 0 ? windowData : [];
  }, [results, pageDomain, activeMarker, country]);

  const hasData = chartData.length > 0;

  // Tabs content - rendered inline to avoid nested component issues
  const tabsContent = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-1 px-1">
        {sortedMarkers.map(marker => {
          const markerHasData = hasDataForMarker(marker.key);
          return (
            <button
              key={marker.key}
              onClick={() => setActiveMarker(marker.key)}
              className={`px-3 py-2 md:px-4 md:py-2 rounded-lg transition-all whitespace-nowrap text-sm md:text-base flex-shrink-0 ${
                activeMarker === marker.key
                  ? 'bg-primary/10 text-primary border-2 border-primary font-semibold md:font-medium'
                  : markerHasData
                    ? 'bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300 font-medium'
                    : 'bg-slate-50 text-slate-400 border-2 border-slate-100 hover:border-slate-200 font-medium'
              }`}
            >
              {marker.label}
            </button>
          );
        })}
      </div>
      <div className="flex-shrink-0 self-end sm:self-auto">
        <TimeWindowSelector selected={timeWindow} onChange={setTimeWindow} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Sticky Tabs - Pure CSS position:sticky for smooth, jitter-free behavior */}
      <div 
        className="bg-slate-50 border-b border-slate-200 -mx-4 px-4 py-2 md:-mx-8 md:px-8 lg:-mx-12 lg:px-12 sticky z-40"
        style={{ 
          top: 'var(--sticky-top, 0px)',
          boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.1)'
        }}
      >
        <style>{`
          /* Normal logged-in site - mobile header is ~44px */
          @media (max-width: 1023px) {
            :root { --sticky-top: 44px; }
          }
          @media (min-width: 1024px) {
            :root { --sticky-top: 0px; }
          }
          /* Preview site - account for the fixed preview banner (~32px) + header (~44px) */
          body.preview-layout {
            --sticky-top: 76px;
          }
          @media (min-width: 1024px) {
            body.preview-layout {
              --sticky-top: 32px;
            }
          }
        `}</style>
        {tabsContent}
      </div>

      {/* Chart Container */}
      <div className="bg-white rounded-t-xl p-4 md:p-6 border-t border-x border-slate-200" onClick={handleChartClick}>
        <div className="mb-3 md:mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-slate-900">{markerConfig.label} Trend ({markerConfig.unit})</h3>
            <p className="text-xs md:text-sm text-slate-500">
              Reference range: {markerConfig.range.min}-{markerConfig.range.max}
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span>Live • {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>
        
        {/* Chart Legend */}
        <div className="mb-3 flex items-center gap-4 text-xs text-slate-600 flex-wrap">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-3 rounded-sm" 
              style={{ backgroundColor: markerConfig.color, opacity: 0.15 }}
            ></div>
            <span>Reference range</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-0.5 rounded-full" 
              style={{ backgroundColor: markerConfig.color }}
            ></div>
            <span>Your results</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border-2" style={{ borderColor: markerConfig.color, backgroundColor: 'white' }}></div>
            <span>Taken while unwell</span>
          </div>
        </div>

        {/* Scrollable Chart Container */}
        <div className="relative">
          <div 
            ref={chartScrollRef}
            onScroll={() => handleHorizontalScroll('chart')}
            className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div style={{ minWidth: pageDomain ? '600px' : '100%' }}>
              <BloodTrendChart
                data={chartData}
                markerConfig={markerConfig}
                showLegend={true}
                pageDomain={pageDomain}
                onDataReady={setChartAxisConfig}
                selectedTreatment={selectedTreatment}
              />
            </div>
          </div>
          
          {/* Mobile swipe hint - minimal fixed height to prevent layout shift */}
          <div className="lg:hidden h-7">
            {showSwipeHint && (
              <div className="flex items-center justify-center gap-2 py-1 bg-slate-100 rounded-md text-slate-500 border border-slate-200 h-full mt-0.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                <span className="text-[11px] font-medium">Swipe to see full chart</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Empty State Message - shows if no data */}
        {!hasData && (
          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-slate-200 text-center">
            <p className="text-xs md:text-sm text-slate-600">No results recorded yet for this marker.</p>
          </div>
        )}
      </div>

      {/* Treatment Timeline - Uses SAME filtered domain - Synced scrolling - Close to chart */}
      {pageDomain && (
        <div 
          ref={timelineScrollRef}
          onScroll={() => handleHorizontalScroll('timeline')}
          className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 -mt-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div style={{ minWidth: '600px' }}>
            <SupplementTimeline
              supplements={filteredSupplements}
              axisConfig={chartAxisConfig || {
                domainStartTs: pageDomain.domainStartTs,
                domainEndTs: pageDomain.domainEndTs,
                ticks: [],
                margin: { left: 40, right: 0 }
              }}
              selectedTreatment={selectedTreatment}
              onTreatmentSelect={handleTreatmentSelect}
            />
          </div>
        </div>
      )}
      
      {/* MANDATORY DISCLAIMER - Pinned beneath timeline, not dismissible */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
        <p className="text-xs text-slate-600 text-center leading-relaxed">
          Trends shown are informational only. Changes in blood counts happen naturally in CLL and do not show whether a treatment is working. Never start or stop any medication or supplement without your haematologist.
        </p>
      </div>
    </div>
  );
};

export default BloodGraphs;
